import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../services/api';
import Navbar from '../components/Navbar';
import OnboardingChecklist from '../components/OnboardingChecklist';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Helper to get local datetime string for datetime-local input
const getLocalDateTimeString = () => {
  const now = new Date();
  const offset = now.getTimezoneOffset();
  const localTime = new Date(now.getTime() - offset * 60 * 1000);
  return localTime.toISOString().slice(0, 16);
};

function Dashboard({ user }) {
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selfVitals, setSelfVitals] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [quickAddPatientId, setQuickAddPatientId] = useState(null);
  const [quickAddForm, setQuickAddForm] = useState({
    vital_type: 'blood_pressure',
    systolic: '',
    diastolic: '',
    sugar_value: '',
    reading_type: 'fasting',
    weight_value: '',
    height_value: '',
    hr_value: '',
    spo2_value: '',
    temp_value: '',
    temp_unit: 'celsius',
    hba1c_value: '',
    recorded_at: getLocalDateTimeString()
  });
  const [quickAddLoading, setQuickAddLoading] = useState(false);
  const [quickAddSuccess, setQuickAddSuccess] = useState('');
  const [quickAddError, setQuickAddError] = useState('');

  const token = localStorage.getItem('token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    window.location.href = '/login';
  };

  const toggleQuickAdd = (patientId) => {
    if (quickAddPatientId === patientId) {
      setQuickAddPatientId(null);
    } else {
      setQuickAddPatientId(patientId);
      setQuickAddSuccess('');
      setQuickAddError('');
      setQuickAddForm({
        vital_type: 'blood_pressure',
        systolic: '',
        diastolic: '',
        sugar_value: '',
        reading_type: 'fasting',
        weight_value: '',
        height_value: '',
        hr_value: '',
        spo2_value: '',
        temp_value: '',
        temp_unit: 'celsius',
        hba1c_value: '',
        recorded_at: getLocalDateTimeString()
      });
    }
  };

  const handleQuickAddSubmit = async (patientId) => {
    setQuickAddLoading(true);
    setQuickAddError('');
    try {
      const payload = {
        patient_id: patientId,
        vital_type: quickAddForm.vital_type,
        recorded_at: quickAddForm.recorded_at
      };

      switch(quickAddForm.vital_type) {
        case 'blood_pressure':
          if (!quickAddForm.systolic || !quickAddForm.diastolic) {
            setQuickAddError('Enter both systolic and diastolic');
            setQuickAddLoading(false);
            return;
          }
          payload.systolic = parseInt(quickAddForm.systolic);
          payload.diastolic = parseInt(quickAddForm.diastolic);
          break;
        case 'blood_sugar':
          if (!quickAddForm.sugar_value) {
            setQuickAddError('Enter blood sugar value');
            setQuickAddLoading(false);
            return;
          }
          payload.sugar_value = parseFloat(quickAddForm.sugar_value);
          payload.reading_type = quickAddForm.reading_type || 'fasting';
          break;
        case 'weight':
          if (!quickAddForm.weight_value) {
            setQuickAddError('Enter weight');
            setQuickAddLoading(false);
            return;
          }
          payload.weight_value = parseFloat(quickAddForm.weight_value);
          break;
        case 'heart_rate':
          if (!quickAddForm.hr_value) {
            setQuickAddError('Enter heart rate');
            setQuickAddLoading(false);
            return;
          }
          payload.hr_value = parseInt(quickAddForm.hr_value);
          break;
        case 'spo2':
          if (!quickAddForm.spo2_value) {
            setQuickAddError('Enter SpO2');
            setQuickAddLoading(false);
            return;
          }
          payload.spo2_value = parseFloat(quickAddForm.spo2_value);
          break;
        case 'temperature':
          if (!quickAddForm.temp_value) {
            setQuickAddError('Enter temperature');
            setQuickAddLoading(false);
            return;
          }
          payload.temp_value = parseFloat(quickAddForm.temp_value);
          payload.temp_unit = quickAddForm.temp_unit || 'celsius';
          break;
        case 'hba1c':
          if (!quickAddForm.hba1c_value) {
            setQuickAddError('Enter HbA1c value');
            setQuickAddLoading(false);
            return;
          }
          payload.vital_type = 'blood_sugar';
          payload.sugar_value = parseFloat(quickAddForm.hba1c_value);
          payload.reading_type = 'hba1c';
          break;
      }

      console.log('Quick add payload:', JSON.stringify(payload));
      await api.post(`${API_URL}/vitals`, payload, { headers });
      setQuickAddSuccess('✅ Vital recorded successfully!');
      setTimeout(() => {
        setQuickAddPatientId(null);
        setQuickAddSuccess('');
      }, 1500);
    } catch (err) {
      setQuickAddError(err.response?.data?.error || 'Failed to save vital');
    } finally {
      setQuickAddLoading(false);
    }
  };

  const fetchPatients = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`${API_URL}/patients`, { headers });
      setPatients(response.data.patients);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load patients');
    } finally {
      setLoading(false);
    }
  }, [headers]);

  const fetchSelfVitals = useCallback(async (patientId) => {
    try {
      const response = await api.get(`${API_URL}/vitals?patient_id=${patientId}`, { headers });
      setSelfVitals(response.data.vitals || []);
    } catch (err) {
      console.error('Failed to fetch vitals:', err);
      setSelfVitals([]);
    }
  }, [headers]);

  const fetchSelfDocuments = useCallback(async (patientId) => {
    try {
      const response = await api.get(`${API_URL}/documents/${patientId}`, { headers });
      setDocuments(response.data.documents || []);
    } catch (err) {
      console.error('Failed to fetch documents:', err);
      setDocuments([]);
    }
  }, [headers]);

  useEffect(() => {
    const loadData = async () => {
      await fetchPatients();
    };
    loadData();
  }, [fetchPatients]);

  useEffect(() => {
    if (patients.length > 0) {
      const selfPatient = patients.find(p => p.relationship_to_admin === 'self') || patients[0];
      if (selfPatient?.patient_id) {
        fetchSelfVitals(selfPatient.patient_id);
        fetchSelfDocuments(selfPatient.patient_id);
      }
    }
  }, [patients, fetchSelfVitals, fetchSelfDocuments]);

  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return null;
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  if (loading) {
    return (
      <div className="dashboard">
        <Navbar user={user} onLogout={handleLogout} />
        <div className="dashboard-content">
          <div className="loading">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <Navbar user={user} onLogout={handleLogout} />

      <div className="dashboard-content">
        <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h2 style={{ color: '#2d3748' }}>Welcome, {user.first_name}!</h2>
          <div style={{ display: 'flex', gap: '12px' }}>
            <Link
              to="/patients/new"
              className="submit-btn"
              style={{ display: 'inline-block', width: 'auto', padding: '12px 24px', textDecoration: 'none', textAlign: 'center' }}
            >
              + Add Patient
            </Link>
          </div>
        </div>

        {/* <OnboardingChecklist
          user={user}
          patients={patients}
          vitals={selfVitals}
          documents={documents}
        /> */}

        {error && <div className="api-error">{error}</div>}

        {patients.length === 0 ? (
          <div className="empty-state" style={{ textAlign: 'center', padding: '60px 20px', background: 'white', borderRadius: '12px' }}>
            <div style={{ fontSize: '4rem', marginBottom: '16px' }}>👤</div>
            <h3 style={{ color: '#2d3748', marginBottom: '8px' }}>No Patient Profiles Yet</h3>
            <p style={{ color: '#718096', marginBottom: '24px', maxWidth: '500px', margin: '0 auto 24px' }}>
              Create patient profiles to start managing health records.
            </p>
            <Link to="/patients/new" className="submit-btn" style={{ padding: '12px 24px', display: 'inline-block' }}>
              Create Your First Patient
            </Link>
          </div>
        ) : (
          <>
            <div style={{ background: 'white', padding: '20px', borderRadius: '12px', marginBottom: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
              <p style={{ color: '#2d3748', fontSize: '1.1rem', margin: 0 }}>
                You have <strong>{patients.length}</strong> patient{patients.length !== 1 ? 's' : ''}. Select a patient to view their health records.
              </p>
            </div>

            <div className="patients-section">
              <div className="patients-grid" style={{ alignItems: 'flex-start' }}>
                {patients.map(patient => {
                  const canEdit = patient.user_id === user.id; // Only own patients can be edited
                  const age = calculateAge(patient.date_of_birth);

                  const relColor = {
                    self: '#2b6cb0',
                    spouse: '#38a169',
                    child: '#38a169',
                    parent: '#7c3aed'
                  }[patient.relationship_to_admin?.toLowerCase()] || '#2b6cb0';

                  const relBadge = {
                    self: { bg: '#ebf8ff', color: '#2b6cb0' },
                    spouse: { bg: '#f0fff4', color: '#38a169' },
                    child: { bg: '#f0fff4', color: '#38a169' },
                    parent: { bg: '#f5f3ff', color: '#7c3aed' }
                  }[patient.relationship_to_admin?.toLowerCase()] || { bg: '#ebf8ff', color: '#2b6cb0' };

                  return (
                    <div key={patient.id} style={{ marginBottom: '20px' }}>
                      <Link
                        to={`/patients/${patient.patient_id}`}
                        style={{ textDecoration: 'none', color: 'inherit' }}
                      >
                        <div
                          className="patient-card"
                          style={{
                            background: 'white',
                            padding: '20px 20px 20px 16px',
                            borderRadius: '12px',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 4px 16px rgba(0,0,0,0.04)',
                            borderLeft: `4px solid ${relColor}`,
                            transition: 'transform 0.2s, box-shadow 0.2s',
                            alignSelf: 'flex-start',
                            cursor: 'pointer'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.12)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.08), 0 4px 16px rgba(0,0,0,0.04)';
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                            <h4 style={{ color: '#1a202c', margin: 0, fontSize: '1.1rem', fontWeight: '700' }}>
                              {patient.first_name} {patient.last_name}
                            </h4>
                            {age !== null && (
                              <span style={{
                                padding: '2px 8px',
                                borderRadius: '10px',
                                fontSize: '0.75rem',
                                fontWeight: '600',
                                backgroundColor: '#ebf8ff',
                                color: '#2b6cb0'
                              }}>{age} yrs</span>
                            )}
                          </div>
                          {patient.relationship_to_admin && (
                            <div style={{ marginBottom: '10px' }}>
                              <span style={{
                                display: 'inline-block',
                                padding: '3px 10px',
                                borderRadius: '10px',
                                fontSize: '0.72rem',
                                fontWeight: '600',
                                backgroundColor: relBadge.bg,
                                color: relBadge.color,
                                textTransform: 'capitalize',
                                letterSpacing: '0.02em'
                              }}>
                                {patient.relationship_to_admin}
                              </span>
                            </div>
                          )}
                          <div className="patient-details" style={{ display: 'flex', flexDirection: 'column', gap: '3px', fontSize: '0.85rem', color: '#718096' }}>
                            {patient.gender && <div>{patient.gender}</div>}
                            {patient.phone && <div>{patient.phone}</div>}
                            {patient.email && <div>{patient.email}</div>}
                          </div>
                          {patient.allergies && (
                            <div className="allergies" style={{ marginTop: '12px', padding: '8px 12px', backgroundColor: '#fff5f5', border: '1px solid #fed7d7', borderRadius: '6px', fontSize: '0.82rem', color: '#c53030' }}>
                              Allergies: {patient.allergies}
                            </div>
                          )}
                          <div style={{ marginTop: '10px', fontSize: '0.72rem', color: '#a0aec0' }}>
                            ID: {patient.patient_id}
                          </div>
                        </div>
                      </Link>

                      {/* Quick Add Button */}
                      <button
                        onClick={() => toggleQuickAdd(patient.patient_id)}
                        style={{
                          width: '100%',
                          padding: '8px',
                          marginTop: '8px',
                          background: quickAddPatientId === patient.patient_id ? '#e2e8f0' : '#f7fafc',
                          border: '1px dashed #cbd5e0',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          color: '#38a169',
                          fontSize: '0.85rem',
                          fontWeight: '600'
                        }}
                      >
                        {quickAddPatientId === patient.patient_id ? 'Cancel' : '+ Quick Add Vital'}
                      </button>

                      {/* Expandable Quick Add Form */}
                      {quickAddPatientId === patient.patient_id && (
                        <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '16px', marginTop: '4px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
                          <h4 style={{ margin: '0 0 12px', color: '#2d3748', fontSize: '0.95rem' }}>⚡ Quick Add Vital for {patient.first_name}</h4>

                          {/* Vital Type Selector - icon buttons */}
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '12px' }}>
                            {[
                              { type: 'blood_pressure', label: '🫀 BP' },
                              { type: 'blood_sugar', label: '🩸 Sugar' },
                              { type: 'hba1c', label: '💉 HbA1c' },
                              { type: 'weight', label: '⚖️ Weight' },
                              { type: 'heart_rate', label: '❤️ HR' },
                              { type: 'spo2', label: '🫁 SpO2' },
                              { type: 'temperature', label: '🌡️ Temp' }
                            ].map(({ type, label }) => (
                              <button
                                key={type}
                                onClick={() => setQuickAddForm({ ...quickAddForm, vital_type: type })}
                                style={{
                                  padding: '6px 12px',
                                  borderRadius: '20px',
                                  border: 'none',
                                  cursor: 'pointer',
                                  fontSize: '0.8rem',
                                  fontWeight: '600',
                                  background: quickAddForm.vital_type === type ? '#2b6cb0' : '#e2e8f0',
                                  color: quickAddForm.vital_type === type ? 'white' : '#4a5568'
                                }}
                              >
                                {label}
                              </button>
                            ))}
                          </div>

                          {/* Dynamic input fields based on vital_type */}
                          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: '10px' }}>
                            {quickAddForm.vital_type === 'blood_pressure' && (<>
                              <div>
                                <label style={{ fontSize: '0.75rem', color: '#718096' }}>Systolic</label>
                                <input type="number" placeholder="120" value={quickAddForm.systolic}
                                  onChange={e => setQuickAddForm({...quickAddForm, systolic: e.target.value})}
                                  style={{ display: 'block', width: '80px', padding: '8px', border: '1px solid #e2e8f0', borderRadius: '6px' }} />
                              </div>
                              <div>
                                <label style={{ fontSize: '0.75rem', color: '#718096' }}>Diastolic</label>
                                <input type="number" placeholder="80" value={quickAddForm.diastolic}
                                  onChange={e => setQuickAddForm({...quickAddForm, diastolic: e.target.value})}
                                  style={{ display: 'block', width: '80px', padding: '8px', border: '1px solid #e2e8f0', borderRadius: '6px' }} />
                              </div>
                            </>)}

                            {quickAddForm.vital_type === 'blood_sugar' && (<>
                              <div>
                                <label style={{ fontSize: '0.75rem', color: '#718096' }}>Value (mg/dL)</label>
                                <input type="number" placeholder="100" value={quickAddForm.sugar_value}
                                  onChange={e => setQuickAddForm({...quickAddForm, sugar_value: e.target.value})}
                                  style={{ display: 'block', width: '100px', padding: '8px', border: '1px solid #e2e8f0', borderRadius: '6px' }} />
                              </div>
                              <div>
                                <label style={{ fontSize: '0.75rem', color: '#718096' }}>Type</label>
                                <select value={quickAddForm.reading_type}
                                  onChange={e => setQuickAddForm({...quickAddForm, reading_type: e.target.value})}
                                  style={{ display: 'block', padding: '8px', border: '1px solid #e2e8f0', borderRadius: '6px' }}>
                                  <option value="fasting">Fasting</option>
                                  <option value="pp">PP</option>
                                  <option value="random">Random</option>
                                </select>
                              </div>
                            </>)}

                            {quickAddForm.vital_type === 'hba1c' && (
                              <div>
                                <label style={{ fontSize: '0.75rem', color: '#718096' }}>HbA1c (%)</label>
                                <input type="number" step="0.1" placeholder="5.7" value={quickAddForm.hba1c_value}
                                  onChange={e => setQuickAddForm({...quickAddForm, hba1c_value: e.target.value})}
                                  style={{ display: 'block', width: '100px', padding: '8px', border: '1px solid #e2e8f0', borderRadius: '6px' }} />
                              </div>
                            )}

                            {quickAddForm.vital_type === 'weight' && (
                              <div>
                                <label style={{ fontSize: '0.75rem', color: '#718096' }}>Weight (kg)</label>
                                <input type="number" step="0.1" placeholder="70" value={quickAddForm.weight_value}
                                  onChange={e => setQuickAddForm({...quickAddForm, weight_value: e.target.value})}
                                  style={{ display: 'block', width: '90px', padding: '8px', border: '1px solid #e2e8f0', borderRadius: '6px' }} />
                              </div>
                            )}

                            {quickAddForm.vital_type === 'heart_rate' && (
                              <div>
                                <label style={{ fontSize: '0.75rem', color: '#718096' }}>BPM</label>
                                <input type="number" placeholder="72" value={quickAddForm.hr_value}
                                  onChange={e => setQuickAddForm({...quickAddForm, hr_value: e.target.value})}
                                  style={{ display: 'block', width: '80px', padding: '8px', border: '1px solid #e2e8f0', borderRadius: '6px' }} />
                              </div>
                            )}

                            {quickAddForm.vital_type === 'spo2' && (
                              <div>
                                <label style={{ fontSize: '0.75rem', color: '#718096' }}>SpO2 (%)</label>
                                <input type="number" step="0.1" placeholder="98" value={quickAddForm.spo2_value}
                                  onChange={e => setQuickAddForm({...quickAddForm, spo2_value: e.target.value})}
                                  style={{ display: 'block', width: '80px', padding: '8px', border: '1px solid #e2e8f0', borderRadius: '6px' }} />
                              </div>
                            )}

                            {quickAddForm.vital_type === 'temperature' && (<>
                              <div>
                                <label style={{ fontSize: '0.75rem', color: '#718096' }}>Temperature</label>
                                <input type="number" step="0.1" placeholder="36.6" value={quickAddForm.temp_value}
                                  onChange={e => setQuickAddForm({...quickAddForm, temp_value: e.target.value})}
                                  style={{ display: 'block', width: '90px', padding: '8px', border: '1px solid #e2e8f0', borderRadius: '6px' }} />
                              </div>
                              <div>
                                <label style={{ fontSize: '0.75rem', color: '#718096' }}>Unit</label>
                                <select value={quickAddForm.temp_unit}
                                  onChange={e => setQuickAddForm({...quickAddForm, temp_unit: e.target.value})}
                                  style={{ display: 'block', padding: '8px', border: '1px solid #e2e8f0', borderRadius: '6px' }}>
                                  <option value="celsius">°C</option>
                                  <option value="fahrenheit">°F</option>
                                </select>
                              </div>
                            </>)}

                            {/* Date/Time field */}
                            <div>
                              <label style={{ fontSize: '0.75rem', color: '#718096' }}>Date & Time</label>
                              <input type="datetime-local" value={quickAddForm.recorded_at}
                                onChange={e => setQuickAddForm({...quickAddForm, recorded_at: e.target.value})}
                                style={{ display: 'block', padding: '8px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '0.85rem' }} />
                            </div>
                          </div>

                          {quickAddError && <p style={{ color: '#e53e3e', fontSize: '0.85rem', margin: '4px 0' }}>{quickAddError}</p>}
                          {quickAddSuccess && <p style={{ color: '#38a169', fontSize: '0.85rem', margin: '4px 0' }}>{quickAddSuccess}</p>}

                          <button
                            onClick={() => handleQuickAddSubmit(patient.patient_id)}
                            disabled={quickAddLoading}
                            style={{ padding: '8px 20px', background: '#2b6cb0', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '0.9rem' }}
                          >
                            {quickAddLoading ? 'Saving...' : 'Save Vital'}
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default Dashboard;